


















































































































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  Ref,
  watch,
} from '@vue/composition-api';
import { useMutation, useQuery, useResult } from '@vue/apollo-composable';
import PrettyCheck from 'pretty-checkbox-vue/check';
import { useRouter, useFormat } from 'vivat';

import CommonIconTop from '@/components/CommonIconTop.vue';
import CommonChip from '@/components/CommonChip.vue';
import CommonDivider from '@/components/CommonDivider.vue';
import CommonPanelWithExpandable from '@/components/CommonPanelWithExpandable.vue';

import vehicleCashflow from '@/table/records/VehicleCashflow';
import useDeliveryAreaCode from '@/useDeliveryAreaCode';
import useAuth from '@/useAuth';
import fuelConsumptionTableOption from '@/table/vehicle/fuelConsumption';

import getVehicle from '@/gql/vehicle/getVehicle.gql';
import getVehicleCashFlows from '@/gql/vehicle/getVehicleCashFlows.gql';
import getVehicleStats from '@/gql/vehicle/getVehicleStats.gql';
import getVehicleDeliveryNotesForCalendar from '@/gql/vehicle/getVehicleDeliveryNotesForCalendar.gql';
import getVehicleCashFlowStats from '@/gql/vehicle/getVehicleCashFlowStats.gql';
import getFuelConsumptions from '@/gql/vehicle/getFuelConsumptions.gql';
import createFuelConsumption from '@/gql/deliveryNote/createFuelConsumption.gql';
import getDrivers from '@/gql/driver/getDriversForSelector.gql';
import getPaymentAccounts from '@/gql/paymentAccount/getPaymentAccounts.gql';

interface CashFlow {
  id: number;
  name: string;
  type: string;
  account: { id: string; name: string; code: string };
  amount: number;
  date: string;
}

const DriverInformation = defineComponent({
  setup() {
    const { me } = useAuth();
    const { route, router } = useRouter();
    const { farthestDeliveryDistanceIndex, distanceTypeToColor } = useDeliveryAreaCode();
    const NOW = new Date();
    const toCurrency = (str: string | number) => useFormat.number(str).currency();
    const toDecimalFormat = (string: string) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Calendar Range
    const calendarStart = ref('');
    const calendarEnd = ref('');
    const calendarCurrentMonth = ref('');
    const calendarCurrentYear = ref('');
    function handleDatesRender(event: any) {
      calendarStart.value = useFormat.date(event.view.activeStart).computerShortDate();
      calendarEnd.value = useFormat.date(event.view.activeEnd).computerShortDate();
      calendarCurrentMonth.value = (event.view.currentStart.getMonth() + 1);
      calendarCurrentYear.value = (event.view.currentStart.getFullYear());
    }

    const filterDate = computed(() => ({
      month: calendarCurrentMonth.value,
      year: calendarCurrentYear.value,
    }));

    const defaultStats = {
      tripsCount: 0, totalDistance: 0, percentWithTrip: 0, percentAbsent: 0, percentRest: 0, expensePerKm: 0,
    };
    const { result: getVehicleStatsResult } = useQuery(getVehicleStats, computed(() => ({
      id: route.value.params.id,
      year: filterDate.value.year || undefined,
      month: filterDate.value.month || undefined,
    })), () => ({ fetchPolicy: 'network-only' }));
    const averageVehicleStats = useResult(getVehicleStatsResult, defaultStats, (data) => data.averageVehicleStats);
    const vehicleStats = useResult(getVehicleStatsResult, defaultStats, (data) => data.vehicle.stats);

    const defaultVehicle = {
      license: '',
      province: '',
      model: '',
      color: '',
      year: '',
      cashFlows: [],
    };
    const vehicle: Ref<any> = ref(defaultVehicle);
    const { result: getVehicleResult, loading } = useQuery(getVehicle, computed(() => ({
      id: route.value.params.id,
    })), () => ({ fetchPolicy: 'network-only' }));
    const vehicleResult = useResult(getVehicleResult, defaultVehicle, (data) => data.vehicle);
    watch(() => vehicleResult.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal) {
        vehicle.value = {
          ...vehicleResult.value,
        };
      }
    });

    const filterCashflowTable = reactive({ searchMaintenance: '', searchOther: '' });
    const getMaintenanceCashflowVariables = computed(() => {
      const vars: {[key: string]: any} = { id: route.value.params.id, filterPaymentAccount: undefined };
      if (filterCashflowTable.searchMaintenance) { vars.filterPaymentAccount = parseInt((filterCashflowTable.searchMaintenance as any).id, 10); }
      return vars;
    });
    const getOtherCashflowVariables = computed(() => {
      const vars: {[key: string]: any} = { id: route.value.params.id, filterPaymentAccount: undefined };
      if (filterCashflowTable.searchOther) { vars.filterPaymentAccount = `%${filterCashflowTable.searchOther}%`; }
      return vars;
    });

    const selectedMaintenanceCashflowItemId: Ref<number[]> = ref([]);
    function selectMaintenanceCashflowItem(id: number) {
      const hitIndex = selectedMaintenanceCashflowItemId.value.indexOf(id);
      if (hitIndex > -1) {
        selectedMaintenanceCashflowItemId.value.splice(hitIndex, 1);
      } else {
        selectedMaintenanceCashflowItemId.value.push(id);
      }
    }
    const selectedCashflowItemId: Ref<number[]> = ref([]);
    function selectCashflowItem(id: number) {
      const hitIndex = selectedCashflowItemId.value.indexOf(id);
      if (hitIndex > -1) {
        selectedCashflowItemId.value.splice(hitIndex, 1);
      } else {
        selectedCashflowItemId.value.push(id);
      }
    }

    // For Chart
    const selectedChartYear = ref(new Date().getFullYear() + 543);
    const vehicleCashFlowStats: Ref<any> = ref({ cashFlowStats: { total: 0, months: [] } });
    const { result: getVehicleCashFlowStatsResult, loading: getVehicleCashFlowStatsLoading } = useQuery(getVehicleCashFlowStats, computed(() => ({
      id: route.value.params.id,
      year: selectedChartYear.value - 543,
    })), () => ({ fetchPolicy: 'network-only' }));
    const vehicleCashFlowStatsResult = useResult(getVehicleCashFlowStatsResult, { cashFlowStats: { total: 0, months: [] } }, (data) => data.vehicle);
    watch(() => vehicleCashFlowStatsResult.value, (newVal, oldVal) => {
      if (!getVehicleCashFlowStatsLoading.value && newVal !== oldVal) {
        vehicleCashFlowStats.value = vehicleCashFlowStatsResult.value;
      }
    });
    const totalExpense = computed(() => useFormat.number(vehicleCashFlowStats.value.cashFlowStats.total).currency());
    const backwardTenYear = Array.from({ length: 10 }, (x, i) => new Date().getFullYear() + 543 - i);
    const thMonthsMini = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
    const computedSeriesData = computed(() => {
      if (vehicleCashFlowStats.value.cashFlowStats.months.length === 0) return [];
      return vehicleCashFlowStats.value.cashFlowStats.months.map((val: any) => ({
        x: thMonthsMini[val.month - 1],
        y: val.sum,
      }));
    });

    const areaCharts = computed(() => ({
      options: {
        dataLabels: { enabled: false },
        yaxis: { opposite: false },
      },
      series: [{
        name: 'ค่าใช้จ่ายประจำปี',
        data: computedSeriesData.value,
      }],
    }));

    // For Calendar
    function navigateToDeliveryNotesDate(date: Date) {
      router.push({
        name: 'DeliveryNoteList',
        params: {
          date: useFormat.date(date).computerShortDate(),
        },
      });
    }
    function navigateToDeliveryNote(event: { item: {[key: string]: any}; index: number }) {
      if (event.item.type === 'deliveryNote') {
        router.push({
          name: 'DeliveryNoteInformation',
          params: {
            id: event.item.id,
          },
        });
      }
    }
    const filterCalendarDueDate = computed(() => ({
      from: `${calendarStart.value}`,
      to: `${calendarEnd.value}`,
    }));
    const { result: calendarQueryResult } = useQuery(getVehicleDeliveryNotesForCalendar, computed(() => ({
      id: route.value.params.id,
      filterCreatedAtBetween: filterCalendarDueDate.value.from ? filterCalendarDueDate.value : undefined,
    })), {
      fetchPolicy: 'cache-and-network',
    });
    const vehicleCalendarInformation = useResult(calendarQueryResult, { deliveryNotes: [], cashFlows: [] }, (data) => data.vehicle);
    const events = computed(() => {
      const result = [...vehicleCalendarInformation.value.deliveryNotes as any[]].map((elm) => {
        const color = distanceTypeToColor(elm.distanceType);
        const distanceArr: any[] = elm.deliveries.map((delivery: any) => delivery?.area?.distanceType || '');
        const farthestIndex = farthestDeliveryDistanceIndex(distanceArr);
        return {
          id: elm.id,
          accountId: '',
          amount: 0,
          title: `${farthestIndex !== null ? `[${elm.deliveries[farthestIndex]?.area?.code || ''}] ` : ''}${elm.uniqueId}`,
          start: useFormat.date(elm.deliveryDate).computerShortDate(),
          type: 'deliveryNote',
          color,
        };
      });

      if (me.company) {
        vehicleCalendarInformation.value.cashFlows.forEach((cashFlow: CashFlow) => {
          const hitIndex = result.findIndex((cashFlowElm: any) => cashFlowElm.accountId === cashFlow.account.id
            && cashFlowElm.start === cashFlow.date);

          let color = '';
          if (cashFlow.account.id === me.company.finePaymentAccount.id) color = 'orange';
          else if (cashFlow.account.id === me.company.feePaymentAccount.id) color = 'pink';
          else if (cashFlow.account.id === me.company.driverAllowancePaymentAccount.id) color = 'yellow';
          if (hitIndex > -1) {
            result[hitIndex].amount += cashFlow.amount;
            result[hitIndex].title = `${toCurrency(result[hitIndex].amount)} - ${cashFlow.name}`;
          } else {
            result.push({
              id: cashFlow.id,
              accountId: cashFlow.account.id,
              amount: cashFlow.amount,
              title: `${toCurrency(cashFlow.amount)} - ${cashFlow.name}`,
              start: cashFlow.date,
              type: 'cashFlow',
              color,
            });
          }
        });
      }

      return result;
    });

    // Fuel consumption
    const addFuelConsumptionModal = ref(null);
    const fuelConsumption = reactive({
      amount: '0',
      date: NOW,
      driver: undefined,
    });
    const createFuelConsumptionMutation = useMutation(createFuelConsumption, () => ({
      variables: {
        date: fuelConsumption.date,
        amount: parseInt(fuelConsumption.amount, 10),
        vehicleId: parseInt(route.value.params.id, 10),
        driverId: fuelConsumption.driver && (fuelConsumption.driver as any).id ? parseInt((fuelConsumption.driver as any).id, 10) : undefined,
      },
      refetchQueries: ['getVehicleDeliveryNotesForCalendar', 'getVehicleCashFlowStats', 'getVehicle', 'getVehicleStats', 'getFuelConsumptions'],
    }));
    createFuelConsumptionMutation.onDone(() => {
      (addFuelConsumptionModal.value as any).close();
    });

    return {
      me,
      toDecimalFormat,
      vehicleStats,
      averageVehicleStats,
      getPaymentAccounts,

      vehicle,

      getVehicleCashFlows,
      vehicleCashflowTableOption: vehicleCashflow,
      getVehicleCashFlowsResult: (data: any) => data.vehicle.paginatedCashFlows,
      filterCashflowTable,

      selectMaintenanceCashflowItem,
      getMaintenanceCashflowVariables,
      selectedMaintenanceCashflowItemId,

      selectCashflowItem,
      getOtherCashflowVariables,
      selectedCashflowItemId,

      // For chart
      backwardTenYear,
      totalExpense,
      selectedChartYear,
      areaCharts,

      // For calendar
      events,
      handleDatesRender,
      navigateToDeliveryNotesDate,
      navigateToDeliveryNote,

      // For FuelConsumption
      getFuelConsumptions,
      fuelConsumptionTableOption,
      getFuelConsumptionResult: (data: any) => data.vehicle.fuelConsumptions,

      addFuelConsumptionModal,
      fuelConsumption,
      createFuelConsumptionMutation,

      getDrivers,
      getDriversResult: (data: any) => data.drivers.data,
    };
  },
  components: {
    CommonIconTop,
    CommonChip,
    CommonDivider,
    PrettyCheck,
    CommonPanelWithExpandable,
  },
});

export default DriverInformation;
